import { useMemo } from "react";

import { KpkBasket, useBasketListPaginated, useCurrentUi } from "@keepeek/commons";
import { useRecoilState, useRecoilValue } from "recoil";

import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import { selectedBasketsWidgetState } from "./atoms";

export enum SelectBasketsState {
  UNSELECTED = "unselected",
  SELECTED = "selected",
  PARTIAL = "partial",
}

export const useBasketsWidget = ({
  widgetId,
  queryFilter,
  sizeParam,
  pageParam,
}: {
  widgetId: string;
  queryFilter: string;
  sizeParam?: number;
  pageParam?: number;
}) => {
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { currentUi } = useCurrentUi();

  const { basketList } = useBasketListPaginated({
    qParam: queryFilter,
    sizeParam,
    pageParam,
    fqParam: activateBasketPartitioning ? [`uiId:${currentUi?.id}`] : [],
  });
  const [selectedBaskets, setSelectedBaskets] = useRecoilState(
    selectedBasketsWidgetState(widgetId),
  );

  const selectAll = () => {
    setSelectedBaskets(basketList?.baskets?.map((b) => b.id) ?? []);
  };

  const unSelectAll = () => {
    setSelectedBaskets([]);
  };

  const toggleSelect = () => {
    if (selectedBaskets.length === 0) {
      selectAll();
    } else {
      unSelectAll();
    }
  };

  const selectedState: SelectBasketsState = useMemo(() => {
    if (selectedBaskets.length === 0) {
      return SelectBasketsState.UNSELECTED;
    }
    if (selectedBaskets.length === basketList.baskets?.length) {
      return SelectBasketsState.SELECTED;
    }
    return SelectBasketsState.PARTIAL;
  }, [basketList.baskets?.length, selectedBaskets.length]);

  return {
    selectAll,
    unSelectAll,
    toggleSelect,
    selectedState,
    basketList,
  };
};

export const useBasketsWidgetBasketSelection = ({
  widgetId,
  basketId,
}: {
  widgetId: string;
  basketId: KpkBasket["id"];
}) => {
  const [selectedBaskets, setSelectedBaskets] = useRecoilState(
    selectedBasketsWidgetState(widgetId),
  );

  const select = () => {
    setSelectedBaskets((prev) => [...prev, basketId]);
  };

  const unSelect = () => {
    setSelectedBaskets((prev) => [...prev].filter((b) => b !== basketId));
  };

  const isSelected = selectedBaskets.includes(basketId);

  const toggle = () => {
    if (isSelected) {
      unSelect();
    } else {
      select();
    }
  };

  return {
    toggle,
    select,
    unSelect,
    isSelected,
  };
};
